import { useEffect, useMemo, useRef, useState } from 'react';

import { Search } from 'lucide-react';

import { InputText } from '../compat-controls';
import { ShowHideAll } from '../show-hide-all';
import { type TailwindColor, UserToggle } from './user-toggle';

export { TailwindColor };

export function ServiceSchedulerViewList<
  T extends {
    name: string;
    id: string;
    color: TailwindColor;
    colorHex?: string | null;
  },
>({
  users,
  searchText: controlledSearchText,
  onSearchTextChange,
  activeUsers: controlledActiveUsers,
  onActiveUsersChange,
}: {
  users: T[];
  searchText?: string;
  onSearchTextChange?: (value: string) => void;
  activeUsers?: Record<string, boolean>;
  onActiveUsersChange?: (activeUsers: Record<string, boolean>) => void;
}) {
  const [internalActiveUsers, setInternalActiveUsers] = useState<
    Record<string, boolean>
  >({});
  const activeUsers = controlledActiveUsers ?? internalActiveUsers;
  const setActiveUsers = onActiveUsersChange ?? setInternalActiveUsers;
  const [internalSearchText, setInternalSearchText] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(320);
  const listRef = useRef<HTMLDivElement>(null);
  const searchText = controlledSearchText ?? internalSearchText;
  const ROW_HEIGHT = 38;
  const OVERSCAN = 8;

  const filteredUsers = useMemo(() => {
    const text = searchText.toLowerCase().trim();
    return users.filter((u) => u.name.toLowerCase().includes(text));
  }, [searchText, users]);

  useEffect(() => {
    if (!listRef.current) return;
    const listEl = listRef.current;
    const resizeObserver = new ResizeObserver(() => {
      setViewportHeight(listEl.clientHeight);
    });
    resizeObserver.observe(listEl);
    setViewportHeight(listEl.clientHeight);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    setScrollTop(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchText]);

  const totalHeight = filteredUsers.length * ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    filteredUsers.length - 1,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN,
  );
  const visibleUsers =
    endIndex >= startIndex ? filteredUsers.slice(startIndex, endIndex + 1) : [];

  return (
    <>
      <div className="relative">
        <Search className="pointer-events-none absolute top-2 right-2 size-4 text-slate-400" />
        <InputText
          placeholder="Search"
          value={searchText}
          className="w-full pr-8 placeholder-slate-400"
          onChange={(e) => {
            const next = e.currentTarget.value;
            if (onSearchTextChange) {
              onSearchTextChange(next);
            } else {
              setInternalSearchText(next);
            }
          }}
        />
      </div>

      <ShowHideAll
        onClickShowAll={() => {
          setActiveUsers(Object.fromEntries(users.map((u) => [u.id, true])));
        }}
        onClickHideAll={() => {
          setActiveUsers({});
        }}
      />

      <div
        ref={listRef}
        className="h-[50cqh] overflow-x-visible overflow-y-auto rounded-md border border-gray-200 bg-white p-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        <div className="relative" style={{ height: totalHeight }}>
          {visibleUsers.map((u, idx) => {
            const rowIndex = startIndex + idx;
            return (
              <div
                key={u.id}
                className="absolute right-0 left-0"
                style={{ top: rowIndex * ROW_HEIGHT }}
              >
                <UserToggle
                  color={u.color}
                  colorHex={u.colorHex}
                  outlined={!activeUsers[u.id]}
                  onClick={() => {
                    setActiveUsers((active) => ({
                      ...active,
                      [u.id]: !active[u.id],
                    }));
                  }}
                >
                  {u.name}
                </UserToggle>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
